'use client'

import { useState, useCallback, useEffect, useContext } from 'react'
import { Card, CardContent } from '@/components/ui/shadcn/card'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { AlertCircle, Info } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import { Badge } from '@/components/ui/shadcn/badge'
import { Loader2 } from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import { slackApi } from '@/lib/api/slackApi'
import SlackOAuthModal from '@/app/manager/slack/components/SlackOAuthModal'
import ConfirmModal from '@/components/common/modals/ConfirmModal'

export default function Integrations() {
  const authContext = useContext(AuthContext)
  const orgId = authContext.user?.organization?.id

  // Slack state
  const [slackConnection, setSlackConnection] = useState(null)
  const [slackLoading, setSlackLoading] = useState(true)
  const [slackError, setSlackError] = useState(null)

  // Modal state
  const [showOAuthModal, setShowOAuthModal] = useState(false)
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)

  const fetchSlackConnection = useCallback(async () => {
    if (!orgId) return
    setSlackLoading(true)
    setSlackError(null)
    try {
      const res = await slackApi.getOrganizationConnection(orgId, authContext)
      if (res?.data) {
        setSlackConnection(res.data.workspace || res.data)
      }
    } catch {
      setSlackConnection(null)
    }
    setSlackLoading(false)
  }, [orgId, authContext])

  useEffect(() => {
    fetchSlackConnection()
  }, [fetchSlackConnection])

  const handleOAuthSuccess = useCallback(() => {
    setShowOAuthModal(false)
    fetchSlackConnection()
  }, [fetchSlackConnection])

  const handleDisconnect = useCallback(async () => {
    try {
      await slackApi.deleteOrganizationConnection(orgId, authContext)
      setSlackConnection(null)
      setShowDisconnectConfirm(false)
    } catch {
      setSlackError('Failed to disconnect Slack workspace')
    }
  }, [orgId, authContext])

  const isConnected = !!slackConnection?.team_id

  return (
    <>
      <div className="grid gap-6">
        {/* Slack Integration Card */}
        <Card className="gap-3 py-4">
          <CardContent className="px-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1">
                <h6 className="text-base font-semibold">Slack</h6>
                <p className="text-sm text-muted-foreground">
                  Connect your Slack workspace to monitor channels during
                  incidents and exercises.
                </p>
              </div>
              <div className="text-right">
                {slackLoading ? (
                  <Loader2 className="size-6 animate-spin" />
                ) : isConnected ? (
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    Connected
                  </Badge>
                ) : (
                  <Badge variant="secondary">Not Connected</Badge>
                )}
              </div>
            </div>

            {slackError && (
              <Alert variant="destructive" className="mb-2">
                <AlertCircle className="size-4" />
                <AlertDescription>{slackError}</AlertDescription>
              </Alert>
            )}

            {!slackLoading && !isConnected && (
              <Button onClick={() => setShowOAuthModal(true)} className="mt-2">
                Connect Slack Workspace
              </Button>
            )}

            {!slackLoading && isConnected && (
              <>
                <Alert className="mb-2">
                  <Info className="size-4" />
                  <AlertDescription>
                    Connected to workspace:{' '}
                    <strong>{slackConnection.team_name}</strong>
                  </AlertDescription>
                </Alert>

                <p className="text-sm text-muted-foreground mb-4">
                  Slack channels can be linked to incidents and exercises
                  directly from their detail pages for real-time message
                  monitoring.
                </p>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowOAuthModal(true)}
                  >
                    Reconnect
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setShowDisconnectConfirm(true)}
                  >
                    Disconnect
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Microsoft Teams Placeholder */}
        <Card className="gap-3 py-4">
          <CardContent className="px-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <h6 className="text-base font-semibold">Microsoft Teams</h6>
                <p className="text-sm text-muted-foreground">
                  Connect your Microsoft Teams workspace for channel monitoring
                  and notifications.
                </p>
              </div>
              <div className="text-right">
                <Badge variant="secondary">Coming Soon</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <SlackOAuthModal
        open={showOAuthModal}
        onClose={() => setShowOAuthModal(false)}
        onSuccess={handleOAuthSuccess}
      />

      <ConfirmModal
        open={showDisconnectConfirm}
        setOpen={setShowDisconnectConfirm}
        title="Disconnect Slack"
        description="Are you sure you want to disconnect your Slack workspace? This will remove all channel configurations."
        acceptButtonText="Disconnect"
        onAccept={handleDisconnect}
      />
    </>
  )
}
